import { useEffect, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  currencyFormatter,
  InvoiceListItem,
  toDecimal,
} from "shared";
import { Tooltip } from "@mui/material";

import { Button } from "../../../common/Atoms/Button";
import { classNames } from "../../../common/lib/classNames";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";

import { InvoiceKanbanCard } from "./InvoiceKanbanCard";

interface InvoiceKanbanColumnProps {
  label: string;
  accentColor: string;
  invoices: InvoiceListItem[];
  count: number;
  outstandingTotalHomeCents: number;
  loadedCount: number;
  hasMore: boolean;
  organisationId: string;
  isLoading?: boolean;
  isFetching?: boolean;
  onLoadMore?: () => void;
  onHeadingClick?: () => void;
  fitContent?: boolean;
}

export function InvoiceKanbanColumn({
  label,
  accentColor,
  invoices,
  count,
  outstandingTotalHomeCents,
  loadedCount,
  hasMore,
  organisationId,
  isLoading = false,
  isFetching = false,
  onLoadMore,
  onHeadingClick,
  fitContent = false,
}: InvoiceKanbanColumnProps) {
  const currentOrg = useGetSelectedOrganisation();
  const [collapsed, setCollapsed] = useState(false);
  const [hasAppliedDefaultCollapse, setHasAppliedDefaultCollapse] = useState(false);
  const outstandingTotalLabel = currencyFormatter(currentOrg?.baseCurrency)(toDecimal(outstandingTotalHomeCents));

  useEffect(() => {
    if (isLoading || hasAppliedDefaultCollapse) {
      return;
    }

    setCollapsed(count === 0);
    setHasAppliedDefaultCollapse(true);
  }, [count, hasAppliedDefaultCollapse, isLoading]);

  if (collapsed) {
    return (
      <section
        className={ `flex w-10 shrink-0 flex-col rounded-lg border border-gray-200 bg-gray-50` }
        aria-label={ `${label} invoices` }
      >
        <button
          type={ `button` }
          onClick={ () => setCollapsed(false) }
          className={ classNames(
            `flex h-full min-h-48 w-full flex-col items-center gap-2 rounded-lg px-1 py-2`,
            `cursor-pointer transition-colors duration-150 hover:bg-gray-100/80`,
            `focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-paidniceGreen-500`,
          ) }
          aria-expanded={ false }
          aria-label={ `Expand ${label} column` }
        >
          <ChevronRightIcon className={ `h-4 w-4 shrink-0 text-gray-400` } />
          <span
            className={ `h-1.5 w-1.5 shrink-0 rounded-full` }
            style={ { backgroundColor: accentColor } }
            aria-hidden={ `true` }
          />
          <span className={ `shrink-0 text-xs font-medium tabular-nums text-gray-500` }>
            { count }
          </span>
          <span
            className={ `shrink-0 text-xs font-medium text-gray-700` }
            style={ {
              writingMode: `vertical-rl`,
              textOrientation: `mixed`,
              transform: `rotate(180deg)`,
            } }
          >
            { label }
          </span>
        </button>
      </section>
    );
  }

  return (
    <section
      className={ classNames(
        `flex w-[320px] shrink-0 flex-col rounded-lg border border-gray-200 bg-gray-50`,
        fitContent ? `` : `h-full min-h-0`,
      ) }
      aria-label={ `${label} invoices` }
    >
      <div className={ `flex shrink-0 items-start justify-between gap-2 px-3 pt-3 pb-2` }>
        <div className={ `min-w-0` }>
          { onHeadingClick ? (
            <Tooltip title={ `View these invoices as a list` }>
              <button
                type={ `button` }
                onClick={ onHeadingClick }
                className={ classNames(
                  `flex min-w-0 items-center gap-2 rounded`,
                  `cursor-pointer group/heading`,
                  `focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-500`,
                ) }
              >
                <span
                  className={ `h-1.5 w-1.5 shrink-0 rounded-full` }
                  style={ { backgroundColor: accentColor } }
                  aria-hidden={ `true` }
                />
                <Paragraph
                  as={ `span` }
                  className={ `truncate text-sm font-medium text-gray-900 group-hover/heading:underline` }
                >
                  { label }
                </Paragraph>
                <span className={ `shrink-0 text-xs tabular-nums text-gray-500` }>
                  { count }
                </span>
              </button>
            </Tooltip>
          ) : (
            <div className={ `flex min-w-0 items-center gap-2` }>
              <span
                className={ `h-1.5 w-1.5 shrink-0 rounded-full` }
                style={ { backgroundColor: accentColor } }
                aria-hidden={ `true` }
              />
              <Paragraph
                className={ `truncate text-sm font-medium text-gray-900` }
              >
                { label }
              </Paragraph>
              <span className={ `shrink-0 text-xs tabular-nums text-gray-500` }>
                { count }
              </span>
            </div>
          ) }
          <Tooltip title={ `Outstanding due in ${currentOrg?.baseCurrency || `home currency`}` }>
            <span className={ `mt-0.5 block truncate text-xs tabular-nums text-gray-500` }>
              { outstandingTotalLabel }
            </span>
          </Tooltip>
        </div>
        <button
          type={ `button` }
          onClick={ () => setCollapsed(true) }
          className={ classNames(
            `shrink-0 rounded p-0.5 text-gray-400`,
            `cursor-pointer transition-colors duration-150 hover:bg-gray-200/60 hover:text-gray-600`,
            `focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-500`,
          ) }
          aria-expanded={ true }
          aria-label={ `Collapse ${label} column` }
        >
          <ChevronLeftIcon className={ `h-4 w-4` } />
        </button>
      </div>

      <div className={ fitContent
        ? `space-y-2 px-3 pb-3`
        : `min-h-0 flex-1 space-y-2 overflow-y-auto px-3 pb-3` }
      >
        { isLoading && invoices.length === 0 ? (
          <div className={ `space-y-2` }>
            <div className={ `h-24 animate-pulse rounded-md border border-gray-200 bg-white` } />
            <div className={ `h-24 animate-pulse rounded-md border border-gray-200 bg-white` } />
            <div className={ `h-24 animate-pulse rounded-md border border-gray-200 bg-white` } />
          </div>
        ) : null }

        { !isLoading && invoices.length === 0 ? (
          <div className={ `rounded-md border border-dashed border-gray-200 bg-white/60 px-3 py-5 text-center` }>
            <Paragraph
              variant={ `secondary` }
              className={ `text-gray-500` }
            >
              { `No unpaid invoices in this bucket.` }
            </Paragraph>
          </div>
        ) : null }

        { invoices.map(invoice => (
          <InvoiceKanbanCard
            key={ invoice.id }
            invoiceListItem={ invoice }
            organisationId={ organisationId }
            accentColor={ accentColor }
          />
        )) }

        { hasMore ? (
          <div className={ `rounded-md border border-dashed border-gray-200 bg-white/60 px-3 py-3` }>
            <Paragraph
              variant={ `secondary` }
              className={ `mb-2 text-xs text-gray-500` }
            >
              { `Showing ${loadedCount} of ${count}` }
            </Paragraph>
            <Button
              size={ `sm` }
              color={ `paidniceGreen` }
              onClick={ onLoadMore }
              disabled={ isFetching || !onLoadMore }
              loading={ isFetching }
              loadingText={ `Loading...` }
            >
              { isFetching ? `Loading...` : `Load more` }
            </Button>
          </div>
        ) : null }
      </div>
    </section>
  );
}
